import React, { useEffect, useState } from 'react';
import Dropdown from 'react-dropdown-aria';
import identity from 'lodash/identity';
import uniqueId from 'lodash/uniqueId';
import FormControlError from '../FormControlError';

import './DropdownField.less';

/**
 * @typedef TDropdownOption
 * @type {import('react-dropdown-aria').Option & {
 *  isDefault?: boolean;
 *  value: string;
 *  title: string;
 *  ariaLabel?: string;
 * }}
*/
const /** @type {TDropdownOption} */DEFAULT_OPTION = {
  isDefault: true,
  value: '',
  title: 'Choose an option',
  ariaLabel: 'Choose an option'
}
/**
 * @param {{
 *  ariaLabel?: string;
 *  ariaDescribedBy?: string;
 *  id?: string;
 *  title?: string;
 *  name?: string;
 *  required?: boolean;
 *  disabled?: boolean;
 *  onChange?(name:string, value:string, option:TDropdownOption|import('react-dropdown-aria').Option):void;
 *  options: TDropdownOption[];
 *  showDefault: boolean;
 *  defaultOptionTitle?:string;
 *  selectedValue?: string|null;
 *  customErrorPrefix?: string|null;
 *  postControlSlot?:((o:null|TDropdownOption) => React.ReactNode) | React.ReactNode;
 * }} props
 * @returns
 */
export const DropdownField = props => {
  const { name, title, onChange, required, disabled, customErrorPrefix, selectedValue, defaultOptionTitle, postControlSlot } = props;
  const [currentValue, setCurrentValue] = useState((selectedValue ?? DEFAULT_OPTION.value).toString());
  const [isDirty, setDirty] = useState(false);
  const [dropDownMaxHeight, setDropDownMaxHeight] = useState(500);
  const [dropDownOpenUp, setDropDownMaxOpenUp] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const refOfDropdown = React.useRef(/** @type {?HTMLDivElement} */(null));
  const inputFieldRef = React.createRef();

  const [controlUID] = useState(props.id || uniqueId(`lsn_ctrl_`));

  /**
   *
   * @param {string|number} value
   */
  const _getNativeErrorMessage = (value) => {
    const isDefault = value === DEFAULT_OPTION.value;
    const valid = required ? !isDefault : true;
    const errMsg = `${customErrorPrefix ?? title} is required`;
    return valid ? '' : errMsg;
  }

  /**
   *
   * @param {string|number} value
   */
  const refreshErrorMessage = (value) => {
    const errorMessage = disabled ? '' : isDirty ? _getNativeErrorMessage(value) : '';
    setErrorMessage(errorMessage);
    // report validity to this control
    errorMessage
      ? inputFieldRef.current?.setCustomValidity(typeof errorMessage === 'string' ? errorMessage : 'This field is invalid')
      : inputFieldRef.current?.setCustomValidity('');
  };

  /**
   *
   * @param {TDropdownOption|import('react-dropdown-aria').Option} option
   */
  const handleChange = option => {
    const { value } = option;
    setDirty(true);
    setCurrentValue(value);
    onChange?.(name ?? '', value, option);
  };

  const getSelectOptions = () => {
    const { options, showDefault } = props;
    return [
      ...(showDefault
        ? [{
          ...DEFAULT_OPTION,
          title: defaultOptionTitle ?? DEFAULT_OPTION.title,
          ariaLabel: defaultOptionTitle ?? DEFAULT_OPTION.ariaLabel,
        }]
        : []),
      ...(options || []),
    ]
      .filter(v => !!v)
      .map((option) => ({
        ...option,
        ariaLabel: option.ariaLabel ?? option.title,
      }));
  };

  const getSelectedOption = () => {
    const options = getSelectOptions();
    const selectedOption =
      (options || []).find(o => (currentValue ?? DEFAULT_OPTION.value).toString().localeCompare(o.value.toString()) === 0) || null;
    return selectedOption;
  };

  const getDefaultOption = () => {
    const options = getSelectOptions();
    return (options || []).find(o => o.isDefault) || null;
  };

  /**
   *
   * @param {TDropdownOption|import('react-dropdown-aria').Option} option
   */
  const isDefaultOption = (option) => {
    if ('isDefault' in option && option.isDefault === true) { return true; }
    return false;
  }

  const renderSelect2 = () => {
    const options = getSelectOptions();
    const selectedOption = getSelectedOption();
    const defaultOption = getDefaultOption();

    return (
      <Dropdown
        options={options}
        ariaLabel={
          `${props.ariaLabel || title} ${selectedOption ? 'is set to ' + selectedOption.title : ''}`.trim() + '.'
        }
        id={controlUID}
        ariaDescribedBy={props.ariaDescribedBy}
        searchable={false}
        value={selectedOption?.value + ''}
        placeholder={defaultOption?.title}
        maxContentHeight={dropDownMaxHeight}
        openUp={dropDownOpenUp}
        onChange={o => handleChange(o)}
        optionItemRenderer={({ option }) => <>{isDefaultOption(option) ? '' : option.title}</>}
        contentClassName="lsn-dropdown-content-new"
        arrowRenderer={(openState) => <i className={`far fa-arrow-down ${openState ? 'opened' : ''}`}></i>}
      />
    );
  };

  const renderDropdown = () => {
    const classNamesWrapper = [
      'lsn-control lsn-dropdown',
      name ? `lsn-control-${name}` : '',
      errorMessage ? 'has-error' : '',
    ].filter(identity).join(' ');

    return <div className={classNamesWrapper} ref={refOfDropdown}>
      <input tabIndex={-1}
        ref={inputFieldRef}
        aria-hidden={true}
        className='drop-down-value-holder'
        defaultValue={currentValue ?? ''}
        required={required}
        onInvalid={() => {
          setDirty(true);
        }}
      />
      {renderSelect2()}
      {required ? <div className='control-error-wrapper'>
        <FormControlError show={isDirty && !!errorMessage}>{errorMessage}</FormControlError>
      </div> : null}
      {typeof postControlSlot === 'function' ? postControlSlot(getSelectedOption()) : postControlSlot}
    </div>
  }

  useEffect(() => {
    const elScope = /** @type {?HTMLBodyElement} */(refOfDropdown.current);
    const options = getSelectOptions();
    const selectedOption = getSelectedOption();
    const elInput = elScope ? elScope.querySelector(':scope input[role="combobox"') : null;
    const elDropDownButton = elScope ? elScope.querySelector(':scope div.dropdown') : null;
    const elValueView = elScope ? elScope.querySelector(':scope .dropdown-selector-value') : null;
    const elListItems = elScope ? elScope.querySelectorAll(':scope span.dropdown-selector-content > div') : null;
    const elListWrapper = elScope ? elScope.querySelector(':scope span.lsn-dropdown-content-new') : null;
    elValueView ? (elValueView.innerHTML = selectedOption ? selectedOption.title : '') : void 0;
    elDropDownButton ? elDropDownButton.removeAttribute('role') : void 0;
    elListItems ? elListItems.forEach(el => el.removeAttribute('aria-label')) : void 0;
    elInput ? elInput.setAttribute('aria-owns', elInput.getAttribute('aria-controls') ?? '') : void 0;
    elInput ? elInput.classList.add('form-input') : void 0;
    elListWrapper ? elListWrapper.setAttribute('tabindex', '0') : void 0;
    const elListItemsSr = elScope ? elScope.querySelectorAll(`:scope div[role="option"]`) : null;
    elListItemsSr?.forEach((el, idx) => {
      const relevantOption = options[idx];
      el.setAttribute('aria-label', relevantOption?.title ?? relevantOption?.ariaLabel ?? '');
    });
    elScope?.querySelectorAll(':scope div.dropdown-option').forEach(el => {
      el.removeAttribute('aria-selected');
    });
  }, [currentValue, props.options]);

  useEffect(() => {
    const selectedOption = getSelectedOption();
    const elScope = /** @type {HTMLDivElement=} */(refOfDropdown.current);
    const elList = elScope ? elScope.querySelector(':scope div[role="listbox"]') : null;
    const elInput = elScope ? elScope.querySelector(':scope input[readonly') : null;
    elList ? elList.setAttribute('aria-label', (selectedOption?.title ?? selectedOption?.ariaLabel ?? 'Nothing')) : void 0;
    elList && props.ariaDescribedBy ? elList.setAttribute('aria-describedby', props.ariaDescribedBy) : void 0;
    const handler = () => {
      setDirty(true);
    };

    const refreshDropDownListDisplay = (/** @type {KeyboardEvent|Event=} */ evt) => {
      if (evt instanceof KeyboardEvent && evt.code !== 'Enter') {
        return;
      }
      const spaceBelow = window.innerHeight - ((elList?.getBoundingClientRect().bottom ?? 0) + 50);
      const dropDownListNextHeght = spaceBelow > 80 ? spaceBelow : 400;
      setDropDownMaxHeight(dropDownListNextHeght);
      setDropDownMaxOpenUp(spaceBelow < 80);
      setTimeout(() => {
        const selectedListOption = /** @type {HTMLDivElement=} */(elScope?.querySelector(':scope div.dropdown-option.selected'));
        elScope?.querySelector(':scope span.dropdown-selector-content')?.scrollTo({
          top: selectedListOption?.offsetTop
        });
      });
    }
    elInput?.addEventListener('focus', handler);
    elInput?.addEventListener('blur', handler);
    elInput?.addEventListener('invalid', handler);
    elScope?.addEventListener('click', refreshDropDownListDisplay);
    elScope?.addEventListener('keydown', refreshDropDownListDisplay);

    // refresh dropdown list height
    return () => {
      elInput?.removeEventListener('focus', handler);
      elInput?.removeEventListener('blur', handler);
      elInput?.removeEventListener('invalid', handler);
      elScope?.removeEventListener('click', refreshDropDownListDisplay);
      elScope?.removeEventListener('keydown', refreshDropDownListDisplay);
    };
  });

  useEffect(() => {
    setCurrentValue((selectedValue ?? '').toString());
    setDirty(!!selectedValue || isDirty);
    inputFieldRef.current ? inputFieldRef.current.value = selectedValue ?? '' : void 0;
    inputFieldRef.current?.dispatchEvent(new InputEvent('input', { data: selectedValue ?? '' }));
    inputFieldRef.current?.dispatchEvent(new Event('change'));
  }, [selectedValue]);

  useEffect(() => {
    refreshErrorMessage(currentValue);
  }, [isDirty, currentValue, disabled, required]);

  return renderDropdown();
};

export default DropdownField;
